import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import * as z from 'zod';

import { Button } from './button.js';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from './form.js';
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSeparator,
  InputOTPSlot,
} from './input-otp.js';

const FormSchema = z.object({
  pin: z.string().min(6, {
    message: 'Su contraseña de un solo uso debe tener 6 caracteres.',
  }),
});

type InputOTPFormProps = {
  label?: string;
  description?: string;
  onSubmit: (otp: string) => void;
  maxLength?: number;
  onResend?: () => void | Promise<void>;
  isLoading?: boolean;
  cooldownTime?: number;
};

export function InputOTPForm({
  label = 'Código de verificación',
  description,
  maxLength = 6,
  onSubmit,
  isLoading,
  onResend,
  cooldownTime = 60,
}: InputOTPFormProps) {
  const [isResending, setIsResending] = useState(false);
  const [cooldownSeconds, setCooldownSeconds] = useState(0);
  const [lastSubmittedValue, setLastSubmittedValue] = useState<string | null>(
    null,
  );

  const form = useForm<z.infer<typeof FormSchema>>({
    resolver: zodResolver(FormSchema),
    defaultValues: {
      pin: '',
    },
  });

  useEffect(() => {
    let intervalId: NodeJS.Timeout;

    if (cooldownSeconds > 0) {
      intervalId = setInterval(() => {
        setCooldownSeconds((prev) => prev - 1);
      }, 1000);
    }

    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [cooldownSeconds]);

  const pinValue = form.watch('pin');

  function handleSubmit(data: z.infer<typeof FormSchema>) {
    onSubmit(data.pin);
  }

  // biome-ignore lint/correctness/useExhaustiveDependencies: handleSubmit y form son estables
  useEffect(() => {
    // Si el usuario borra caracteres, resetear el lastSubmittedValue
    if (pinValue && pinValue.length < maxLength && lastSubmittedValue) {
      setLastSubmittedValue(null);
    }

    if (
      pinValue &&
      pinValue.length === maxLength &&
      !isLoading &&
      pinValue !== lastSubmittedValue
    ) {
      setLastSubmittedValue(pinValue);
      form.handleSubmit(handleSubmit)();
    }
  }, [pinValue, maxLength, isLoading, lastSubmittedValue]);

  async function handleResend() {
    if (!onResend || cooldownSeconds > 0 || isResending) return;

    setIsResending(true);
    try {
      await onResend();
      setCooldownSeconds(cooldownTime);
      form.reset();
      setLastSubmittedValue(null);
    } catch (error) {
      console.error('Error al reenviar código:', error);
    } finally {
      setIsResending(false);
    }
  }

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(handleSubmit)}
        className="w-full space-y-6"
      >
        <FormField
          control={form.control}
          name="pin"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{label}</FormLabel>
              <FormControl>
                <InputOTP maxLength={6} {...field} className="w-full">
                  <InputOTPGroup className="w-full justify-between">
                    <InputOTPSlot index={0} className="flex-1" />
                    <InputOTPSlot index={1} className="flex-1" />
                    <InputOTPSlot index={2} className="flex-1" />
                    <InputOTPSeparator />
                    <InputOTPSlot index={3} className="flex-1" />
                    <InputOTPSlot index={4} className="flex-1" />
                    <InputOTPSlot index={5} className="flex-1" />
                  </InputOTPGroup>
                </InputOTP>
              </FormControl>
              {description && <FormDescription>{description}</FormDescription>}
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="space-y-3">
          <Button
            type="submit"
            className="w-full h-12 font-medium rounded-xl shadow-sm"
            disabled={!pinValue || pinValue.length !== maxLength || isLoading}
          >
            {isLoading ? 'Verificando...' : 'Verificar código'}
          </Button>

          <p className="text-center text-xs text-muted-foreground">
            ¿No recibiste el código?{' '}
            <button
              type="button"
              onClick={handleResend}
              disabled={cooldownSeconds > 0 || isResending || !onResend}
              className={`underline ${
                cooldownSeconds > 0 || isResending || !onResend
                  ? 'text-muted-foreground cursor-not-allowed'
                  : 'text-cyan-600 hover:text-cyan-700'
              }`}
            >
              {isResending
                ? 'Reenviando...'
                : cooldownSeconds > 0
                  ? `Reenviar en ${cooldownSeconds}s`
                  : 'Reenviar'}
            </button>
          </p>
        </div>
      </form>
    </Form>
  );
}
